<template>
	<div :class="['groups-drawer', { 'is-open': activeStep }]" ref="root">
		<div class="groups-drawer__step-title-wrapper" :class="{ 'is-open': activeStep === 1 }">
			<StepTitle text="Welcome to Live Yes Connect Groups" :is-disabled="activeStep !== 1">
				<button v-show="activeStep === 1"
					class="groups-drawer__cancel-button"
					role="button"
					:tabindex="activeStep === 1 ? '0' : '-1'"
					@keydown.shift.tab.prevent="jumpToLastElement($event)"
					@click="$emit('cancel')"
					@keydown.enter.prevent="$emit('cancel')"
				>
					Cancel
				</button>
			</StepTitle>
		</div>

		<div :class="['groups-drawer__wrapper', { 'is-open': activeStep === 1 }]">
			<div class="groups-drawer__label">{{ drawerLabel }}</div>

			<div class="groups-drawer__list-wrapper">
				<a v-for="(location, index) of locationsList"
					class="groups-drawer__location"
					:key="index"
					:tabindex="activeStep === 1 ? '0' : '-1'"
					@click.prevent="$emit('select-group', location)"
					@keydown.enter.prevent="$emit('select-group', location)"
					@keydown.tab="jumpToFirstElement($event)"
				>
					{{ location.locationName }}
				</a>
			</div>
		</div>

		<div class="groups-drawer__step-title-wrapper" :class="{ 'is-open': activeStep === 2 }">
			<StepTitle number="2" :text="stepTwoLabel" :is-disabled="activeStep !== 2">
				<button v-if="activeStep === 2"
					class="groups-drawer__cancel-button"
					role="button"
					:tabindex="activeStep === 2 ? '0' : '-1'"
					@keydown.shift.tab.prevent="jumpToLastElement($event)"
					@click="handleCancelClick()"
					@keydown.enter.prevent="handleCancelClick()"
				>
					Cancel
				</button>
			</StepTitle>
		</div>

		<div :class="['groups-drawer__wrapper', { 'is-open': activeStep === 2 }]">
			<div class="groups-drawer__label">{{ drawerLabel }}</div>

			<div v-if="activeGroupType === 'locations'" class="groups-drawer__list-wrapper">
				<a v-for="(subGroup, index) of subGroups"
					class="groups-drawer__group"
					:key="index"
					:tabindex="activeStep === 2 ? '0' : '-1'"
					:href="subGroup.url"
					@keydown.enter="$emit('select-subgroup', subGroup)"
					@keydown.tab="jumpToFirstElement($event)"
				>
					{{ subGroup.title }}
				</a>
			</div>

			<div v-else class="groups-drawer__topic-wrapper">
				<div v-for="(topic, index) of topicsList"
					class="groups-drawer__group-wrapper"
					:key="index"
					@keydown.enter="$emit('select-group', topic)"
					@keydown.tab="jumpToFirstElement($event)"
				>
					<div class="groups-drawer__group-title">
						{{ topic.groupName }}
					</div>

					<a v-for="(subGroup, groupIndex) of topic.subGroups"
						:key="groupIndex"
						class="groups-drawer__group"
						:tabindex="activeStep === 2 ? '0' : '-1'"
						:href="subGroup.url"
					>
					{{ subGroup.title }}

					</a>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
	import StepTitle from '@/components/StepTitle.vue';

	export default {
		name: 'GroupsDrawer',

		components: { StepTitle },

		props: [
			'topicsList', // Array
			'locationsList', // Array
			'activeGroup', // Object
			'activeGroupType', // String
			'activeStep', // Number
		],

		data() {
			return {
				focusableElements: [],
			};
		},

		watch: {
			'activeStep': {
				immediate: true,
				handler(stepNumber) {
					const isOpen = !!stepNumber;

					if (!isOpen) return;

					this.$nextTick(() => {
						this.getFocusableContent();
						this.focusableElements[0].focus();
					});
				}
			},
		},

		computed: {
			subGroups() {
				return this.activeGroup && this.activeGroup.subGroups;
			},

			drawerLabel() {
				if (!this.activeGroupType) return;

				const isSelectingLocation = this.activeGroupType === 'locations';

				if (isSelectingLocation) {
					if (this.activeStep === 2) return 'Select Your Group';

					return 'Select a State';
				}

				return 'Select your Topic Group';
			},

			stepTwoLabel() {
				if (this.activeGroupType === 'locations') {
					let baseText = 'Find Your Group';
					const activeLocation = this.activeGroup && this.activeGroup.locationName;

					if (activeLocation) return baseText += `: ${activeLocation}`;

					return baseText;
				}

				return 'Connect Groups by Topic';
			},
		},

		methods: {
			handleCancelClick() {
				if (this.activeGroupType === 'locations') {
					this.$emit('go-to-previous-step');

					return;
				}

				this.$emit('cancel');
			},

			getFocusableContent() {
				const rootElement = this.$refs.root;

				if (!rootElement) return;

				this.focusableElements = rootElement.querySelectorAll('button:not([tabindex="-1"]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
			},

			jumpToFirstElement(event) {
				const element = event.target;
				const isLastElement = element === this.focusableElements[this.focusableElements.length - 1];

				if (isLastElement && !event.shiftKey) {
					event.preventDefault();
					this.focusableElements[0].focus();
				}
			},

			jumpToLastElement() {
				if (!this.focusableElements || !this.focusableElements.length) this.getFocusableContent();

				this.focusableElements[this.focusableElements.length - 1].focus();
			},
		},
	}
</script>

<style lang="scss" scoped>
	@import '@/styles/variables';
	@import '@/styles/mixins';

	$base-padding: 16px;
	$large-padding: 64px;

	.groups-drawer {
		position: absolute;
		background-color: $lightGray;
		top: 0;
		left: 0;
		right: 0;
		height: 0vh;
		display: flex;
		flex-direction: column;
		pointer-events: none;
		opacity: 0;
		transition: height 0.2s ease;
		z-index: 0;

		&.is-open {
			opacity: 1;
			pointer-events: all;
			// 80px is to account for height of the "donate" bar at the bottom of the page. 174px is height of the header.
			height: calc(100vh - 80px - 174px);
			width: 100%;
			z-index: 2;
		}

		&__wrapper {
			overflow-y: scroll;
			height: 0;
			flex: 0;
			padding: 0;
			transition: height 0.25s ease-in-out;

			&.is-open {
				transition: height 0.25s ease-in-out;
				transition: padding-top 0.25s ease-in-out;
				transition: padding-bottom 0.25s ease-in-out;
				padding: 18px 40px;
				height: 100%;
				flex: 1;
			}
		}

		&__label {
			color: $darkGray;
			text-transform: capitalize;
			margin-bottom: 16px;
		}

		&__list-wrapper {
			column-count: 3;

			@media(max-width: $screen-sm) {
				column-count: 2;
			}
		}

		&__location {
			display: block;
			font-weight: 500;
			margin-bottom: 8px;
		}

		&__step-title-wrapper {
			background-color: $darkBlue;
			padding: $base-padding calc(#{$base-padding} * 2);

			&.is-open{
				background-color: $lightBlue;
			}

			&:not(.is-open) {
				@include background-polka-dot();
			}
		}

		&__cancel-button {
			@include button-cancel();
			margin-left: 12px;
		}

		&__topic-wrapper {
			display: flex;
			gap: 32px;

			@media(max-width: $screen-sm) {
				flex-direction: column;
				gap: 24px;
			}
		}

		&__group-title {
			color: $lightBlue;
			margin-bottom: 16px;
			text-transform: uppercase;
			font-weight: 500;
		}

		&__group-wrapper {
			flex: 1;
		}

		&__group {
			display: block;
			font-weight: 500;
			margin-bottom: 8px;
		}

		@media(max-width: $screen-md) {
			&__label {
				margin-bottom: 16px;
			}

			&__wrapper {
				&.is-open {
					padding: 24px 16px;
					height: fit-content;
					overflow: scroll;
				}
			}

			&__list-wrapper {
				height: unset;
				grid-auto-flow: row;
				row-gap: 18px;
			}
		}
	}
</style>
